
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Download, Plus, Users } from "lucide-react";
import { TeamMember } from "../types";

// Sample data - in a real app, this would come from an API
const teamMembers: TeamMember[] = [
  {
    id: "1",
    name: "Alex Johnson",
    role: "Frontend Developer",
    allocation: 85,
    skills: ["React", "TypeScript", "CSS"],
    avatar: "https://i.pravatar.cc/150?u=1",
  },
  {
    id: "2",
    name: "Sam Lee",
    role: "Backend Developer",
    allocation: 100,
    skills: ["Node.js", "PostgreSQL", "APIs"],
    avatar: "https://i.pravatar.cc/150?u=2",
  },
  {
    id: "3",
    name: "Jordan Smith",
    role: "UI/UX Designer",
    allocation: 65,
    skills: ["Figma", "User Research", "Prototyping"],
    avatar: "https://i.pravatar.cc/150?u=3",
  },
  {
    id: "4",
    name: "Taylor Wong",
    role: "Project Manager",
    allocation: 90,
    skills: ["Scrum", "Jira", "Client Management"],
    avatar: "https://i.pravatar.cc/150?u=4",
  },
];

export function WorkforceOverview() {
  // Calculate stats
  const totalTeamMembers = teamMembers.length;
  const averageAllocation = Math.round(
    teamMembers.reduce((sum, member) => sum + member.allocation, 0) / totalTeamMembers
  );
  
  // Find overallocated team members
  const overallocatedMembers = teamMembers.filter(member => member.allocation > 90);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Workforce Planning</h1>
        <div className="flex gap-3">
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Team Member
          </Button>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Team Size</CardTitle>
            <CardDescription>Total active team members</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Users className="h-5 w-5 text-muted-foreground mr-2" />
              <span className="text-3xl font-bold">{totalTeamMembers}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Average Allocation</CardTitle>
            <CardDescription>Team capacity utilization</CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <span className="text-3xl font-bold">{averageAllocation}%</span>
              <Progress value={averageAllocation} className="h-2 mt-2" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Overallocated</CardTitle>
            <CardDescription>Team members at risk</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <span className="text-3xl font-bold text-amber-500">{overallocatedMembers.length}</span>
              <span className="text-muted-foreground ml-2">of {totalTeamMembers}</span>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {overallocatedMembers.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Attention Required</AlertTitle>
          <AlertDescription>
            {overallocatedMembers.length} team member{overallocatedMembers.length > 1 ? 's are' : ' is'} overallocated (&gt;90%). 
            Consider redistributing workload to prevent burnout.
          </AlertDescription>
        </Alert>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle>Team Resource Allocation</CardTitle>
          <CardDescription>Current allocation of team members across projects</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Team Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Skills</TableHead>
                <TableHead className="text-right">Allocation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <img 
                      src={member.avatar} 
                      alt={member.name} 
                      className="w-8 h-8 rounded-full"
                    />
                    {member.name}
                  </TableCell>
                  <TableCell>{member.role}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {member.skills.map((skill) => (
                        <Badge key={skill} variant="outline" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end">
                      <span className={`font-medium ${member.allocation > 90 ? 'text-red-500' : ''}`}>
                        {member.allocation}%
                      </span>
                      <Progress 
                        value={member.allocation} 
                        className={`h-2 w-24 mt-1 ${member.allocation > 90 ? 'bg-red-200' : ''}`}
                        indicatorClassName={member.allocation > 90 ? 'bg-red-500' : ''}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
